import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, ScanFace, ListChecks, Gauge, FileText, ScrollText, Check, Lock, Scale, BadgeCheck, Menu, X, Code2 } from "lucide-react";
import { BrandIcon } from "../components/BrandLogo";
import { api, errMsg } from "../lib/api";

const GOLD = "#E0A526";
const NAV = [["#producto", "Producto"], ["#como-funciona", "Cómo funciona"], ["#integracion", "Integración"]];

const Logo = () => (
  <a href="#inicio" className="flex items-center gap-2" data-testid="landing-logo">
    <BrandIcon className="h-8 w-8" />
    <span className="font-display text-xl font-bold text-white tracking-tight">Inti<span style={{ color: GOLD }}>Check</span></span>
  </a>
);

const DemoBtn = ({ className = "", testId }) => (
  <a href="#demo" className={`lp-btn-gold inline-flex items-center justify-center min-h-[50px] px-6 rounded-full font-semibold text-[#070D18] ${className}`} data-testid={testId}>Pide tu demo</a>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="fixed top-0 inset-x-0 z-40 border-b border-white/10 bg-[#070D18]/70 backdrop-blur-xl" data-testid="landing-header">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-5 sm:px-8 py-3.5 min-h-[72px]">
        <Logo />
        <nav className="hidden md:flex items-center gap-8 text-sm text-slate-300">
          {NAV.map(([h, l]) => <a key={h} href={h} className="hover:text-white transition-colors" data-testid={`landing-nav-${h.slice(1)}`}>{l}</a>)}
          <Link to="/ingresar" className="hover:text-white transition-colors" data-testid="landing-header-login">Iniciar sesión</Link>
          <DemoBtn className="min-h-[40px] px-5 text-sm" testId="landing-header-demo" />
        </nav>
        <button className="md:hidden text-white p-2" onClick={() => setOpen(!open)} aria-label="Menú" data-testid="landing-menu-toggle">{open ? <X /> : <Menu />}</button>
      </div>
      {open && (
        <nav className="md:hidden flex flex-col gap-4 px-5 pb-5 text-slate-200" data-testid="landing-mobile-menu">
          {NAV.map(([h, l]) => <a key={h} href={h} onClick={() => setOpen(false)}>{l}</a>)}
          <Link to="/ingresar">Iniciar sesión</Link>
          <DemoBtn testId="landing-mobile-demo" />
        </nav>
      )}
    </header>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3 py-2 border-b border-white/5 last:border-0">
    <span className="text-slate-300 text-sm">{label}</span>
    <span className="flex items-center gap-1 text-sm font-semibold text-emerald-400">{value ?? <Check size={16} />}</span>
  </div>
);

const EjemploCard = () => (
  <div className="relative lp-rise w-[300px] sm:w-[330px]" style={{ animationDelay: "350ms" }} data-testid="landing-example-card">
    <div className="relative rounded-3xl border border-white/10 bg-[#0D1626]/90 backdrop-blur-xl p-4 shadow-[0_30px_80px_-10px_rgba(0,0,0,0.85)]">
      <div className="flex items-center justify-between mb-4">
        <span className="flex items-center gap-2 text-sm text-slate-300"><span className="lp-pulse h-2.5 w-2.5 rounded-full bg-emerald-400" /> Verificación en curso</span>
        <span className="text-[11px] uppercase tracking-widest px-2 py-1 rounded-full border border-white/15 text-slate-400" data-testid="landing-example-badge">Ejemplo</span>
      </div>
      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#1B2A41] to-[#0F1A2C] p-3 h-28" data-testid="landing-example-doc">
        <div className="flex gap-3">
          <div className="h-16 w-14 rounded-lg bg-white/10 grid place-items-center"><ScanFace className="text-slate-400" size={26} /></div>
          <div className="flex-1 space-y-2 pt-1">{[80, 60, 70, 45].map((w) => <div key={w} className="h-2.5 rounded bg-white/10" style={{ width: `${w}%` }} />)}</div>
        </div>
        <div className="absolute bottom-3 left-4 right-4 font-mono text-[10px] text-slate-500 tracking-widest truncate">IDXXX&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;</div>
        <div className="lp-scan absolute inset-x-0 h-12" aria-hidden />
      </div>
      <div className="mt-2">
        <Row label="Prueba de vida" />
        <Row label="Rostro = documento" />
        <Row label="Listas ONU · OFAC · PEP" value="Sin coincidencias" />
        <Row label="Nivel de riesgo" value="Bajo" />
      </div>
      <div className="mt-3 rounded-2xl border border-[#E0A526]/30 bg-[#E0A526]/10 p-3" data-testid="landing-example-pending">
        <p className="text-sm font-semibold" style={{ color: GOLD }}>Pendiente de tu aprobación</p>
        <p className="text-xs text-slate-300">El oficial decide; queda en bitácora.</p>
      </div>
    </div>
  </div>
);

const HeroVisual = () => (
  <div className="relative mx-auto w-full max-w-[460px] lg:max-w-none pb-56 sm:pb-48 lg:pb-0" data-testid="landing-hero-visual">
    <div className="absolute inset-8 rounded-full bg-[#E0A526]/15 blur-3xl" aria-hidden />
    <img src="/portada-celular.webp" alt="Celular verificando el rostro de una persona con prueba de vida" width="720" height="1073"
      className="relative lp-rise ml-auto w-[78%] lg:w-[80%] rounded-[2rem] object-cover [mask-image:radial-gradient(ellipse_at_center,#000_62%,transparent_100%)]" data-testid="landing-hero-phone" />
    <div className="absolute left-0 bottom-0 lg:-left-6 lg:bottom-6">
      <EjemploCard />
    </div>
  </div>
);

const Hero = () => (
  <section id="inicio" className="relative min-h-[88svh] flex items-center pt-28 pb-10 lg:pt-24 lg:pb-8 overflow-hidden" data-testid="landing-hero">
    <img src="/portada-inticheck.webp" alt="" className="absolute inset-0 h-full w-full object-cover opacity-60" aria-hidden />
    <div className="absolute inset-0 bg-gradient-to-r from-[#070D18] via-[#070D18]/85 to-[#070D18]/40" aria-hidden />
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1.15fr_1fr] gap-10 items-center w-full">
      <div className="lp-rise">
        <span className="inline-flex items-center gap-2 text-xs sm:text-sm px-3 py-1.5 rounded-full border border-[#E0A526]/40 text-[#F3C766] bg-[#E0A526]/10" data-testid="landing-hero-tag">
          <ShieldCheck size={15} /> KYC + cumplimiento UAF en una sola plataforma
        </span>
        <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-white" data-testid="landing-hero-title">
          Verifica a tus clientes y ordena tu cumplimiento UAF <span className="lp-gold-text">desde el primer día</span>
        </h1>
        <p className="mt-5 text-base sm:text-lg text-slate-300 max-w-xl leading-relaxed" data-testid="landing-hero-subtitle">
          Documento, selfie con prueba de vida y revisión de listas ONU, OFAC y PEP desde el celular de tu cliente. Tú decides; todo queda registrado.
        </p>
        <div className="mt-7 flex flex-col sm:flex-row gap-3">
          <DemoBtn testId="landing-hero-demo" />
          <Link to="/registro" className="inline-flex items-center justify-center min-h-[50px] px-6 rounded-full border border-white/20 text-white font-semibold hover:bg-white/10 transition-colors" data-testid="landing-hero-register">Registrar mi empresa</Link>
        </div>
      </div>
      <HeroVisual />
    </div>
  </section>
);

const CONFIANZA = [[BadgeCheck, "Verificación facial certificada con prueba de vida"], [Scale, "Circular UAF N° 62"], [ShieldCheck, "Preparado para la Ley 21.719"], [Lock, "Datos cifrados"]];

const Confianza = () => (
  <section className="border-y border-white/10 bg-[#0A1322]" data-testid="landing-trust">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
      {CONFIANZA.map(([I, t]) => <p key={t} className="flex items-center gap-2 text-sm text-slate-300"><I size={18} style={{ color: GOLD }} className="shrink-0" /> {t}</p>)}
    </div>
  </section>
);

const Titulo = ({ over, title, id }) => (
  <div className="max-w-2xl">
    <p className="text-xs font-semibold uppercase tracking-[0.25em]" style={{ color: GOLD }}>{over}</p>
    <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white mt-3" data-testid={`landing-title-${id}`}>{title}</h2>
  </div>
);

const PRODUCTO = [
  [ScanFace, "Identidad", "Documento + selfie con prueba de vida."],
  [ListChecks, "Listas ONU, OFAC y PEP", "Revisión al registrar y cada día."],
  [Gauge, "Riesgo automático", "Nivel bajo, medio o alto con su motivo."],
  [FileText, "Ficha UAF completa en PDF", "Lista para presentar."],
  [ScrollText, "Bitácora", "Quién aprobó, cuándo y por qué."],
];

const Producto = () => (
  <section id="producto" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="landing-product">
    <div className="grid lg:grid-cols-2 gap-10 items-center mb-20" data-testid="landing-audience">
      <p className="font-display text-2xl sm:text-3xl text-white leading-snug"><span style={{ color: GOLD }}>Para quién:</span> remesadoras, casas de cambio y empresas que deben verificar a sus clientes.</p>
      <img src="/local-envios.webp" alt="Encargado de un local de envíos atendiendo a un cliente que muestra su celular" width="1100" height="738" loading="lazy"
        className="w-full rounded-3xl border border-white/10 object-cover shadow-2xl" data-testid="landing-audience-image" />
    </div>
    <Titulo over="Producto" title="Todo lo que pide tu cumplimiento, en un solo lugar" id="producto" />
    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {PRODUCTO.map(([I, t, d], i) => (
        <div key={t} className={`lp-card rounded-3xl border border-white/10 bg-white/[0.03] p-6 ${i === 0 ? "lg:row-span-2 lg:flex lg:flex-col lg:justify-end" : ""}`} data-testid={`landing-product-${i}`}>
          <div className="h-11 w-11 rounded-2xl grid place-items-center bg-[#E0A526]/15 mb-5"><I size={22} style={{ color: GOLD }} /></div>
          <h3 className="font-display text-lg font-semibold text-white">{t}</h3>
          <p className="text-sm text-slate-400 mt-1">{d}</p>
        </div>
      ))}
    </div>
  </section>
);

const PASOS = [
  ["01", "Registras tu empresa y subes tu logo"],
  ["02", "Compartes tu link por WhatsApp; el cliente se verifica desde su celular sin descargar nada"],
  ["03", "Apruebas desde tu panel"],
];

const Como = () => (
  <section id="como-funciona" className="bg-[#0A1322] border-y border-white/10" data-testid="landing-how">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
      <Titulo over="Cómo funciona" title="Tres pasos, sin instalar nada" id="como" />
      <ol className="mt-12 grid md:grid-cols-3 gap-6">
        {PASOS.map(([n, t]) => (
          <li key={n} className="relative rounded-3xl border border-white/10 p-6 pt-14" data-testid={`landing-step-${n}`}>
            <span className="absolute top-5 left-6 font-display text-3xl font-bold lp-gold-text">{n}</span>
            <p className="text-base text-slate-200 leading-relaxed">{t}</p>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

const EJEMPLO_API = `GET /api/integracion/clientes/{external_ref}
X-API-Key: ••••••••

{
  "inticheck_id": "ejemplo-0000",
  "estado": "aprobado",
  "nivel_riesgo": "bajo",
  "limite_mensual_clp": null,
  "fecha_aprobacion": "2026-01-15T14:02:00Z",
  "fecha_vencimiento_ficha": "2027-01-15T14:02:00Z"
}`;

const Integracion = () => (
  <section id="integracion" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28 grid lg:grid-cols-2 gap-12 items-center" data-testid="landing-integration">
    <div>
      <Titulo over="Integración" title="¿Ya tienes app de envíos? Conéctala por API." id="integracion" />
      <p className="mt-6 text-base text-slate-300 leading-relaxed">Tu app consulta el estado de cada cliente y recibe un aviso firmado cuando cambia. <b className="text-white">IntiCheck no procesa pagos ni mueve dinero.</b></p>
    </div>
    <div className="rounded-3xl border border-white/10 bg-[#050A13] overflow-hidden" data-testid="landing-api-example">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 text-xs text-slate-400"><Code2 size={14} /> Respuesta de ejemplo</div>
      <pre className="p-5 text-[12px] sm:text-sm leading-relaxed text-slate-300 font-mono overflow-x-auto">{EJEMPLO_API}</pre>
    </div>
  </section>
);

const Campo = ({ id, label, ...p }) => (
  <label htmlFor={id} className="block">
    <span className="text-sm text-slate-300">{label}</span>
    <input id={id} required {...p} className="mt-1 w-full min-h-[48px] rounded-xl bg-white/5 border border-white/15 px-4 text-white placeholder:text-slate-500 focus:outline-none focus:border-[#E0A526]" data-testid={`demo-${id}`} />
  </label>
);

const Demo = () => {
  const [f, setF] = useState({ nombre: "", empresa: "", whatsapp: "", correo: "" });
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const b = (k) => ({ value: f[k], onChange: (e) => setF({ ...f, [k]: e.target.value }) });
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/public/demo", f);
      setOk(true);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section id="demo" className="relative bg-[#0A1322] border-t border-white/10 overflow-hidden" data-testid="landing-demo">
      <div className="absolute -top-40 right-0 h-96 w-96 rounded-full bg-[#E0A526]/10 blur-3xl" aria-hidden />
      <div className="relative max-w-3xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <Titulo over="Pide tu demo" title="Te mostramos IntiCheck con tu caso" id="demo" />
        {ok ? (
          <p className="mt-10 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-5 text-emerald-300" data-testid="demo-ok">¡Gracias! Te contactaremos pronto.</p>
        ) : (
          <form onSubmit={send} className="mt-10 grid sm:grid-cols-2 gap-4">
            <Campo id="nombre" label="Nombre" autoComplete="name" {...b("nombre")} />
            <Campo id="empresa" label="Empresa" autoComplete="organization" {...b("empresa")} />
            <Campo id="whatsapp" label="WhatsApp" type="tel" placeholder="+56 9 …" autoComplete="tel" {...b("whatsapp")} />
            <Campo id="correo" label="Correo" type="email" autoComplete="email" {...b("correo")} />
            <p className="sm:col-span-2 text-sm text-slate-400">Usaremos estos datos solo para responder tu solicitud.</p>
            <button type="submit" disabled={busy} className="lp-btn-gold sm:col-span-2 min-h-[52px] rounded-full font-semibold text-[#070D18] disabled:opacity-60" data-testid="demo-submit">{busy ? "Enviando…" : "Pide tu demo"}</button>
          </form>
        )}
      </div>
    </section>
  );
};

const Footer = () => (
  <footer className="border-t border-white/10 bg-[#070D18]" data-testid="landing-footer">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between text-sm text-slate-400">
      <p>IntiCheck — Un servicio de Vitsil SpA · Copiapó, Chile</p>
      <nav className="flex gap-5">
        <Link to="/privacidad" className="hover:text-white" data-testid="landing-privacy-link">Política de privacidad</Link>
        <Link to="/terminos" className="hover:text-white" data-testid="landing-terms-link">Términos de uso</Link>
        <a href="#demo" className="hover:text-white" data-testid="landing-contact-link">Contacto</a>
      </nav>
    </div>
  </footer>
);

export default function Landing() {
  return (
    <div className="landing min-h-screen bg-[#070D18] text-slate-200 font-body" data-testid="landing-page">
      <div className="lp-grid fixed inset-0 pointer-events-none" aria-hidden />
      <Header />
      <main className="relative">
        <Hero />
        <Confianza />
        <Producto />
        <Como />
        <Integracion />
        <Demo />
      </main>
      <Footer />
    </div>
  );
}
